import React, { FunctionComponent, useState, useCallback, useContext, useMemo } from "react";
import { Theme, makeStyles, AccordionDetails, Grid, AccordionSummary } from "@material-ui/core";
import { MenuItemCategory, MenuItemSubcategory } from "../../../models/types/category-types";
import { TopBarCategoryAccordion } from "./category-accordion/top-bar-category-accordion";
import { useTranslation } from "react-i18next";
import { TopBarAccordionHeader } from "./tob-bar-accordion-header";
import { ResultsIcon } from "../../../assets/icons/results-icon";
import { NutritionContext } from "../../../pages/nutrition/context/nutrition-context";
import { CustomAccordion } from "./custom-accordion";
import { PersonIcon } from "../../../assets/icons/person-icon";
import { ShopIcon } from "../../../assets/icons/shop-icon";
import { FavoritesIcon } from "../../../assets/icons/favorites-icon";
import { IdCardIcon } from "../../../assets/icons/id-card-icon";
import { LaboratoryIcon } from "../../../assets/icons/laboratory-icon";
import { useUser } from "../../user/user-context";
import { ConfirmDialog } from "../../dialog/confirm-dialog";
import { getLocalizedLinks } from "../../../translations/links";
import { i18n } from "../../../translations/i18n";
import { theme } from "../../../theme/theme";
import { CATEGORY_MENU_FONT_SIZE } from "../../menu/category-item-menu";

export const TOP_BAR_ACCORDION_INSET = 10;

export interface ITopBarAccordionItem {
  Icon?: FunctionComponent;
  text: string;
  onClick?: () => void;
  isActive?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  accordionRoot: {
    backgroundColor: "transparent",
    borderBottom: 0,
    "&:before": {
      display: "none",
    },
    "&$expanded": {
      margin: "auto",
    },
  },
  menuContainer: {
    backgroundColor: theme.palette.background.default,
  },
  baseAccordionDetailsRoot: {
    boxShadow: "0 8px 4px -4px rgba(0, 0, 0, 0.17)",
    padding: 0,
    "&$expanded": {
      margin: "auto",
    },
  },
  accordionDetailsRoot: {
    background: "rgba(0, 0, 0, 0.02)",
    padding: 0,
    "&$expanded": {
      margin: "auto",
    },
  },
  accordionDetailsExpanded: {
    padding: 0,
  },
  accordionsContainer: {
    paddingLeft: theme.spacing(TOP_BAR_ACCORDION_INSET),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  logoutButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    borderRadius: 10,
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
    },
  },
}));

interface ITopBarAccordionProps {
  open: boolean;
  onClose: () => void;
}

type Accordions = "results" | "profile" | false;

export const TopBarAccordion: FunctionComponent<ITopBarAccordionProps> = (props) => {
  const { open, onClose } = props;
  const { logout } = useUser();
  const classes = useStyles({});
  const { t } = useTranslation();
  const { activeCategory, activeSubcategory, changeActiveCategories } = useContext(NutritionContext);
  const productPageLink = useMemo(() => getLocalizedLinks(i18n).MY_DNA_PRODUCT_PAGE, []);

  const [openAccordion, setOpenAccordion] = useState<Accordions>(false);
  const handleCategoryAccordionChange = useCallback(
    (accordion: Accordions) => (event: React.ChangeEvent<{}>, newExpanded: boolean) => {
      setOpenAccordion(newExpanded ? accordion : false);
    },
    [setOpenAccordion],
  );

  const [logoutModalOpen, setLogoutModalOpen] = useState<boolean>(false);
  const toggleLogoutModal = useCallback(() => setLogoutModalOpen(!logoutModalOpen), [
    logoutModalOpen,
    setLogoutModalOpen,
  ]);

  const handleYourData = useCallback(() => {
    changeActiveCategories(MenuItemCategory.USER, MenuItemSubcategory.USER_DATA);
  }, [changeActiveCategories]);

  const handleFavourites = useCallback(() => {
    changeActiveCategories(MenuItemCategory.USER, MenuItemSubcategory.FAVOURITES);
  }, [changeActiveCategories]);

  const handleYourReports = useCallback(() => {
    changeActiveCategories(MenuItemCategory.USER, MenuItemSubcategory.USER_REPORTS);
  }, [changeActiveCategories]);

  const profileButtons = [
    {
      text: t("app_bar.favorites"),
      icon: FavoritesIcon,
      onClick: handleFavourites,
      isActive: activeSubcategory === MenuItemSubcategory.FAVOURITES,
    },
    {
      text: t("app_bar.your_data"),
      icon: IdCardIcon,
      onClick: handleYourData,
      isActive: activeSubcategory === MenuItemSubcategory.USER_DATA,
    },
    {
      text: t("app_bar.your_reports"),
      icon: LaboratoryIcon,
      onClick: handleYourReports,
      isActive: activeSubcategory === MenuItemSubcategory.USER_REPORTS,
    },
    {
      text: t("app_bar.logout"),
      onClick: toggleLogoutModal,
      className: classes.logoutButton,
      color: "primary",
      variant: "contained",
      textColor: theme.palette.common.white,
    },
  ];

  return (
    <>
      <CustomAccordion expanded={open} elevation={0}>
        <AccordionSummary style={{ display: "none" }}></AccordionSummary>
        <AccordionDetails classes={{ root: classes.baseAccordionDetailsRoot }}>
          <Grid container className={classes.menuContainer}>
            <Grid item xs={12}>
              <CustomAccordion
                expanded={openAccordion === "results"}
                onChange={handleCategoryAccordionChange("results")}
                elevation={0}
              >
                <TopBarAccordionHeader
                  key={"top-bar-accordion-header"}
                  Icon={ResultsIcon}
                  active={activeCategory !== MenuItemCategory.USER}
                  open={openAccordion === "results"}
                >
                  {t("app_bar.results")}
                </TopBarAccordionHeader>
                <AccordionDetails key={"top-bar-accordion-details"} classes={{ root: classes.accordionDetailsRoot }}>
                  <Grid container className={classes.accordionsContainer}>
                    <TopBarCategoryAccordion onChangeCategory={onClose} />
                  </Grid>
                </AccordionDetails>
              </CustomAccordion>
            </Grid>
            <Grid item xs={12}>
              <TopBarAccordionHeader
                Icon={ShopIcon}
                active={false}
                open={false}
                onClick={() => {
                  window.open(productPageLink, "_blank");
                }}
              >
                {t("app_bar.shop")}
              </TopBarAccordionHeader>
            </Grid>
            <Grid item xs={12}>
              <CustomAccordion
                expanded={openAccordion === "profile"}
                onChange={handleCategoryAccordionChange("profile")}
                elevation={0}
              >
                <TopBarAccordionHeader
                  Icon={PersonIcon}
                  active={activeCategory === MenuItemCategory.USER}
                  open={openAccordion === "profile"}
                >
                  {t("app_bar.profile")}
                </TopBarAccordionHeader>
                <AccordionDetails classes={{ root: classes.accordionDetailsRoot }}>
                  <Grid container className={classes.accordionsContainer}>
                    {profileButtons.map((button, idx) => (
                      <Grid item xs={12} key={`profile-button-${idx}`}>
                        <TopBarAccordionHeader
                          Icon={button.icon}
                          active={!!button.isActive}
                          open={false}
                          onClick={() => {
                            onClose();
                            button.onClick();
                          }}
                          className={button.className}
                          iconSize={22}
                          textColor={button.textColor}
                          textSize={CATEGORY_MENU_FONT_SIZE - 2}
                          textWeight={700}
                        >
                          {button.text}
                        </TopBarAccordionHeader>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </CustomAccordion>
            </Grid>
          </Grid>
        </AccordionDetails>
      </CustomAccordion>
      <ConfirmDialog
        heading={t("app_bar.logout_modal.heading")}
        confirmText={t("general.logout")}
        onConfirm={logout}
        onClose={toggleLogoutModal}
        open={logoutModalOpen}
      />
    </>
  );
};
